'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { Modal, Spinner } from '@benzinga/core-ui';
import { LoginPrompt } from './loginPrompt';
import { useIsUserTrialing } from './hooks/useIsUserTrialing';
import { useIsUserLoggedIn } from './hooks/useIsUserLoggedIn';
import { useHasUserTrialed } from './hooks/useHasUserTrialed';
import { SessionContext } from '@benzinga/session-context';
import { SubscriptionsManager } from '@benzinga/subscription-manager';
import { useUserHasSubscription } from './hooks/useUserHasSubscription';
import { ProductsManager, ProductPackage } from '@benzinga/product-manager';
import { Slug } from './entity';
import { openWindow } from '@benzinga/frontend-utils';
import { AuthenticationManager, LoggingManager } from '@benzinga/session';
import { useUser } from './hooks';
import { Trial } from '@benzinga/themed-icons';
interface Props {
  componentName?: string;
  disableTrial?: boolean;
  slug?: Slug;
  onClose?: (upgraded: boolean) => void;
}

const DEFAULT_SLUG = {
  feature: '',
  package: 'bzpro-essential',
  plan: 'bzpro-essential-monthly',
  product: 'benzinga-pro',
};

const PRICING_BASE_URL = 'https://www.benzinga.com/pro/pricing';
const UTM_AD_TYPE = 'pro-paywall';

const buildPricingUrl = (campaignType: 'anonymous' | 'basic' | 'free', componentName?: string): string => {
  const params = new URLSearchParams({
    utm_adType: UTM_AD_TYPE,
    utm_campaign: `pro-${campaignType}-upsell`,
    ...(componentName && { utm_ad: componentName }),
  });
  return `${PRICING_BASE_URL}?${params.toString()}`;
};

export const UpSellPrompt: React.FC<Props> = props => {
  const slug = React.useMemo(() => ({ ...DEFAULT_SLUG, ...props?.slug }), [props?.slug]);
  const session = React.useContext(SessionContext);
  const authenticationManager = session.getManager(AuthenticationManager);
  const user = useUser();
  const isUserLoggedIn = useIsUserLoggedIn();
  const isUserTrialing = useIsUserTrialing(slug?.['product'] ?? 'benzinga-pro');
  const hasUserTrialed = useHasUserTrialed(slug?.['product'] ?? 'benzinga-pro');
  const hasSubscription = useUserHasSubscription(slug?.['product'] ?? 'benzinga-pro');

  const [activeLoginModel, setActiveLoginModel] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleOpenLogin = React.useCallback(() => {
    setActiveLoginModel(true);
  }, []);

  const onClose = React.useCallback(() => {
    const onClose = props.onClose;
    onClose?.(false);
  }, [props.onClose]);

  const [packages, setPackages] = React.useState<ProductPackage[]>([]);
  React.useEffect(() => {
    (async () => {
      if (slug?.['product']) {
        setPackages((await session.getManager(ProductsManager).getProduct(slug?.['product'])).ok?.packages ?? []);
      }
    })();
  }, [slug, session]);

  const redirectToPricing = React.useCallback(() => {
    const url = slug?.['package'] && packages.find(p => p?.slug === slug?.['package'])?.checkoutUrl;
    const campaignType = props.disableTrial || hasUserTrialed || isUserTrialing || hasSubscription ? 'basic' : 'free';
    openWindow(url || buildPricingUrl(campaignType, props.componentName), '_blank');
  }, [hasUserTrialed, isUserTrialing, packages, props.componentName, props.disableTrial, slug, hasSubscription]);

  const redirectToRegister = React.useCallback(() => {
    if (!isUserLoggedIn) {
      openWindow(buildPricingUrl('anonymous', props.componentName));
    } else {
      const campaignType = props.disableTrial || hasUserTrialed || isUserTrialing || hasSubscription ? 'basic' : 'free';
      openWindow(buildPricingUrl(campaignType, props.componentName));
    }
    onClose();
  }, [
    isUserLoggedIn,
    props.componentName,
    props.disableTrial,
    hasUserTrialed,
    isUserTrialing,
    hasSubscription,
    onClose,
  ]);

  const redirectToTrial = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const subscriptionManager = session.getManager(SubscriptionsManager);
      if (!user?.humanVerified && !user?.smsVerified) {
        redirectToRegister();
        return;
      }
      const res = await subscriptionManager.createSubscription(slug?.['plan'], true, undefined);
      if (res.err) {
        setIsLoading(false);
        session.getManager(LoggingManager).log(
          'error',
          {
            category: 'upsell',
            message: res.err.message,
          },
          ['toast'],
        );
      } else {
        setIsLoading(false);
        authenticationManager.getAuthSession(true);
        onClose();
        window.location.href = '/dashboard';
      }
    } catch (error) {
      setIsLoading(false);
    }
  }, [session, user?.humanVerified, user?.smsVerified, slug, redirectToRegister, authenticationManager, onClose]);

  return !activeLoginModel ? (
    <Modal
      fullScreen={true}
      onClose={onClose}
      size={{ coverScreen: true }}
      title={'More opportunities are within reach'}
    >
      <NoAuthContainer>
        <IconDiv>
          <Trial fill="#3e81f6" />
        </IconDiv>
        <h1>More tools, more opportunities!</h1>
        <Paragraph>
          Get news 15 times faster and open a world of discovery with advanced tools. Use them to stay ahead of the
          crowd and find new opportunities every. Single. Day.
        </Paragraph>

        {isUserLoggedIn ? (
          props.disableTrial || hasUserTrialed || isUserTrialing || hasSubscription ? (
            <TrialBtn onClick={redirectToPricing}>Upgrade Your Subscription</TrialBtn>
          ) : isLoading ? (
            <SpinnerContent>
              <Spinner />
            </SpinnerContent>
          ) : (
            <TrialBtn disabled={isLoading} onClick={redirectToTrial}>
              Start Your 2-Week Free Trial
            </TrialBtn>
          )
        ) : (
          <>
            <TrialBtn onClick={redirectToRegister}>Start Your 2-Week Free Trial</TrialBtn>
            <ActionBtn onClick={redirectToPricing}>View Our Pricing</ActionBtn>
            <ActionBtn onClick={handleOpenLogin}>Already registered? Login</ActionBtn>
          </>
        )}
      </NoAuthContainer>
    </Modal>
  ) : (
    <LoginPrompt onClose={props.onClose} />
  );
};

const Paragraph = styled.p`
  max-width: 900px;
`;

const NoAuthContainer = styled.div`
  width: 80%;
  margin: 0px auto;
  height: 90vh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 1rem;
  text-align: center;
  color: ${props => props.theme.colors.foreground} !important;
  h1 {
    font-size: 3rem;
    font-weight: bolder;
    color: ${props => props.theme.colors.foreground} !important;
  }
  p {
    font-size: 1.5rem;
    font-weight: 700;
  }
  @media (max-width: 768px) {
    width: 100%;
    h1 {
      font-size: 2rem;
    }
    p {
      font-size: 1.2rem;
    }
  }
`;
const SpinnerContent = styled.span`
  display: block;
  font-size: 12px;
`;
const TrialBtn = styled.button`
  font-size: 2.5rem;
  font-weight: bold;
  padding: 1rem 2rem;
  background-color: ${props => props.theme.colors.brandMuted};
  color: ${props => props.theme.colors.foregroundActive};
  border-radius: 2rem;
  @media (max-width: 768px) {
    font-size: 1.5rem;
    padding: 1rem;
  }
`;

const ActionBtn = styled.button`
  font-size: 17px;
  text-align: center;
  color: ${props => props.theme.colors.foreground} !important;
  &:hover {
    color: #3e81f6 !important;
  }
`;

const IconDiv = styled.div`
  color: #3e81f6;
  & svg {
    height: 12vw;
    font-size: 1rem !important;
  }
`;
